import { Modal, ModalProps } from '@/modules/common/components/Modal';
import { EmployerService } from '@/services/employer';
import { Button, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';
import { MouseEvent } from 'react';
import { useTeamDeletion } from '../../hooks/useTeams';

type DeleteTeamDialog = Omit<ModalProps, 'children' | 'title' | 'subTitle'> & {
  onSuccess: () => void;
  team: string;
};

export function DeleteTeamDialog({
  onSuccess,
  team,
  ...props
}: DeleteTeamDialog) {
  const { mutateAsync: deleteTeam, isPending } = useTeamDeletion(team);
  const queryClient = useQueryClient();

  const handleDeleteTeam = async () => {
    try {
      await deleteTeam(team);
      await queryClient.invalidateQueries({
        queryKey: [EmployerService.PATH],
        exact: false,
      });
      onSuccess();
      enqueueSnackbar(`Team deleted successfuly`, {
        variant: 'success',
      });
    } catch {
      enqueueSnackbar('An error occurred. Please try again later.', {
        variant: 'error',
      });
    }
  };

  const handleCancel = (ev: MouseEvent) => {
    props.onClose?.(ev, 'backdropClick');
  };

  return (
    <Modal
      {...props}
      title={`Delete Team`}
      onClose={(...args) => {
        props.onClose?.(...args);
      }}
      classes={{
        paper: 'max-w-lg mx-auto p-8 pb-3',
      }}
      actions={
        <>
          <Button
            variant="outlined"
            type="button"
            size="small"
            className="w-32"
            onClick={handleCancel}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button
            color="error"
            size="small"
            variant="contained"
            type="button"
            className="w-32"
            loading={isPending}
            onClick={handleDeleteTeam}
          >
            Delete
          </Button>
        </>
      }
    >
      <div>
        <Typography className="font-semibold">
          Are you sure you want to delete this team?
        </Typography>
        <p>
          By clicking 'Delete' you will permanently delete this team and it's
          team members will be unassigned to it. Are you sure you want to
          proceed?
        </p>
      </div>
    </Modal>
  );
}
